/** @file TileScreen: one Tile tab: tool column, the zoomed tile canvas, and the facts panel. */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DragEvent, ReactElement } from 'react';
import { useStore } from 'zustand';
import { artClient } from '../../panels/art/artClient';
import type { ArtDeps } from '../../panels/art/artTypes';
import type { TileRef } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { SplitPane } from '../../ui-kit/SplitPane';
import { tileAzimuth } from './tileAzimuth';
import { TileCanvas } from './TileCanvas';
import { tileDraftKey } from './tileDraftKey';
import { tileDrafts } from './tileDrafts';
import { TileFacts } from './TileFacts';
import { tileFitZoom } from './tileFitZoom';
import { tileKeyHandlers } from './tileKeyHandlers';
import { tileOnionImages } from './tileOnionImages';
import { tilePaintUi } from './tilePaintUi';
import { tilePalette } from './tilePalette';
import { TilePrompts } from './TilePrompts';
import { tileRequestClose } from './tileRequestClose';
import { tileSave } from './tileSave';
import { tileStartImage } from './tileStartImage';
import { tileTakeImage } from './tileTakeImage';
import { TileToolColumn } from './TileToolColumn';
import { tileZoomStep } from './tileZoomStep';
import { useTileKeys } from './useTileKeys';
import { useTilePaste } from './useTilePaste';

/** `deps` replaces the api client built from `session.url` (dev pages and tests have no server). */
export type TileScreenProps = { deps?: Pick<ArtDeps, 'uploadImage'> };

/** A centred line of text instead of the screen. */
const Note = ({ text }: { text: string }): ReactElement => <div className="p3-screen p3-muted" style={{ padding: 24 }}>{text}</div>;

/** The tab's body; keyed by the draft key, so switching tabs starts fresh local state (zoom, stroke). */
function TileBody({ puppetId, tile, puppet, deps }: { puppetId: string; tile: TileRef; puppet: Puppet; deps: TileScreenProps['deps'] }): ReactElement {
  const store = useEditorStore();
  const url = useEditor((s) => s.session.url);
  const bundle = useEditor((s) => s.preview.bundle);
  const upload = useMemo(() => (deps ?? artClient(url)).uploadImage, [deps, url]);
  const draftKey = tileDraftKey(puppetId, tile);
  const key = slotKey(tile.drawable, tile.entry, tile.tile);
  const slots = useMemo(() => deriveSlots(puppet), [puppet]);
  const slot = slots.find((s) => s.key === key);
  const entrySlots = useMemo(() => slots.filter((s) => s.drawable === tile.drawable && s.entry === tile.entry), [slots, tile.drawable, tile.entry]);
  const drafts = useStore(tileDrafts, (s) => s.drafts);
  const draft = drafts[draftKey];
  const onionOn = useStore(tilePaintUi, (s) => s.onion);
  const grid = useStore(tilePaintUi, (s) => s.grid);
  const [area, setArea] = useState<[number, number]>([0, 0]);
  const [zoomSet, setZoomSet] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const size = slot?.size ?? [1, 1];
  const zoom = zoomSet ?? tileFitZoom(size, area);
  const palette = useMemo(() => tilePalette(bundle?.sheets ?? []), [bundle]);
  useEffect(() => {
    const image = slot === undefined || tileDrafts.getState().drafts[draftKey] !== undefined ? null : tileStartImage(slot, bundle);
    if (image !== null) tileDrafts.getState().start(draftKey, image);
  }, [draftKey, slot, bundle]);
  const save = useCallback(async (): Promise<boolean> => {
    const d = tileDrafts.getState().drafts[draftKey];
    if (d === undefined || d.version === d.savedVersion) return true;
    setSaving(true);
    const ok = await tileSave(store, upload, key, d.image);
    if (ok) tileDrafts.getState().saved(draftKey, d.version);
    setSaving(false);
    return ok;
  }, [draftKey, key, store, upload]);
  const notifyError = useCallback((text: string): void => { store.getState().notify('error', text); }, [store]);
  const zoomBy = (dir: -1 | 0 | 1): void => { setZoomSet(dir === 0 ? null : tileZoomStep(zoom, dir)); };
  useTilePaste(draftKey, size, notifyError);
  useTileKeys(tileKeyHandlers({ draftKey, zoom: zoomBy, save: () => { void save(); }, close: () => { tileRequestClose(store, tile); } }));
  if (slot === undefined) return <Note text={`This puppet has no tile ${key} (the part or its entry is gone). Close the tab.`} />;
  if (draft === undefined) return <Note text="Waiting for the preview to cut the tile from…" />;
  const onion = onionOn ? tileOnionImages(slot, entrySlots, bundle, (s) => drafts[tileDraftKey(puppetId, s)]?.image) : [];
  const drop = (e: DragEvent): void => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file !== undefined) void tileTakeImage(draftKey, size, file).then((error) => { if (error !== null) notifyError(error); });
  };
  return (
    <div className="p3-tile-screen" data-screen="tile" data-slot={key}>
      <TileToolColumn draftKey={draftKey} zoom={zoom} dirty={draft.version !== draft.savedVersion} saving={saving} palette={palette}
        onZoom={zoomBy} onSave={() => { void save(); }} />
      <SplitPane direction="row" fixed="end" initial={300} min={220} max={520} label="Tile canvas and facts">
        <div className="p3-tile-drop" onDragOver={(e) => { e.preventDefault(); }} onDrop={drop}>
          <TileCanvas draftKey={draftKey} image={draft.image} pivot={slot.pivot} zoom={zoom} onion={onion} grid={grid}
            onZoom={setZoomSet} onArea={setArea} />
        </div>
        <TileFacts slot={slot} azimuth={tileAzimuth(puppet, tile)} entrySlots={entrySlots} bundle={bundle}
          onOpen={(ref) => { store.getState().openTile(ref); }} />
      </SplitPane>
      <TilePrompts draftKey={draftKey} slot={key} size={size} onSave={save} onClose={() => { store.getState().closeTile(tile); }} />
    </div>
  );
}

/** The Tile screen of the route's tab; it waits for the puppet document before it shows the tile. */
export function TileScreen({ deps }: TileScreenProps): ReactElement {
  const route = useEditor((s) => s.route);
  const puppet = useEditor((s) => s.puppet.doc?.puppet ?? null);
  if (route.screen !== 'tile') return <Note text="No tile is open." />;
  if (puppet === null) return <Note text="Loading the puppet…" />;
  const key = tileDraftKey(route.puppetId, route.tile);
  return <TileBody key={key} puppetId={route.puppetId} tile={route.tile} puppet={puppet} deps={deps} />;
}
